import { useEffect, useRef } from "react";
import { useGoogleMap } from "@react-google-maps/api";
import { GoogleMapsOverlay } from "@deck.gl/google-maps";
import { PathLayer, ScatterplotLayer } from "@deck.gl/layers";
import { PathStyleExtension } from "@deck.gl/extensions";

import { estimatedRuns } from "../../services/environmentAggregation";

// Spessore della polyline, in pixel: "bella spessa", resta tale a ogni zoom.
export const PATH_WIDTH_PX = 9;
export const SAMPLE_RADIUS_PX = 5;
// Tratti stimati (buchi GPS): trattino bianco e spazio colorato, in multipli dello spessore,
// quindi in pixel a ogni zoom.
const ESTIMATED_DASH = [1.5, 2];
const WHITE = [255, 255, 255];
const DOT_BORDER = [40, 40, 40];

/**
 * Overlay deck.gl sopra la mappa Google per i dati ambientali: una polyline a segmenti già
 * colorati e i pallini dei campioni già colorati. Chi lo usa decide come calcolare colori e
 * posizioni (nel tempo per una sessione singola, nello spazio per la sessione comune). I segmenti
 * e i pallini `estimated` (posizione stimata in un buco GPS) si disegnano tratteggiati e cavi.
 *
 * @param segments [{ path: [[lng, lat], [lng, lat]], color: [r, g, b], estimated? }]
 * @param dots     [{ lat, lng, color: [r, g, b], estimated?, ...dati per il tooltip }]
 * @param bounds   [{ lat, lng }] punti su cui inquadrare la mappa, una volta sola quando arrivano
 * @param getTooltipText (dot) => string | null
 */
export default function EnvironmentOverlay({ segments, dots, bounds, getTooltipText }) {
    const map = useGoogleMap();
    const overlayRef = useRef(null);
    const tooltipRef = useRef(getTooltipText);
    tooltipRef.current = getTooltipText;

    useEffect(() => {
        if (!map) return undefined;
        const overlay = new GoogleMapsOverlay({
            layers: [],
            getTooltip: ({ object }) => {
                if (!object || !tooltipRef.current) return null;
                const text = tooltipRef.current(object);
                return text ? { text } : null;
            },
        });
        overlay.setMap(map);
        overlayRef.current = overlay;
        return () => {
            overlay.setMap(null);
            overlayRef.current = null;
        };
    }, [map]);

    // Inquadra una volta, quando arrivano i punti.
    useEffect(() => {
        if (!map || !bounds || bounds.length === 0) return;
        const box = new window.google.maps.LatLngBounds();
        bounds.forEach(p => box.extend({ lat: p.lat, lng: p.lng }));
        map.fitBounds(box, 48);
    }, [map, bounds]);

    useEffect(() => {
        if (!overlayRef.current) return;
        const layers = [
            new PathLayer({
                id: "environment-path",
                data: segments || [],
                getPath: d => d.path,
                getColor: d => d.color,
                getWidth: PATH_WIDTH_PX,
                widthUnits: "pixels",
                widthMinPixels: PATH_WIDTH_PX,
                capRounded: true,
                jointRounded: true,
                opacity: 0.95,
            }),
            // Sui tratti stimati una linea bianca tratteggiata, un percorso per tratto continuo: nei
            // suoi spazi si vede il colore sotto, cioè un tratteggio colorato regolare a ogni zoom.
            new PathLayer({
                id: "environment-path-estimated",
                data: estimatedRuns(segments),
                getPath: d => d.path,
                getColor: WHITE,
                getWidth: PATH_WIDTH_PX,
                widthUnits: "pixels",
                widthMinPixels: PATH_WIDTH_PX,
                // trattini dritti: con le punte arrotondate gli spazi si chiuderebbero
                capRounded: false,
                jointRounded: true,
                getDashArray: ESTIMATED_DASH,
                dashJustified: true,
                extensions: [new PathStyleExtension({ dash: true })],
            }),
            new ScatterplotLayer({
                id: "environment-samples",
                data: dots || [],
                getPosition: d => [d.lng, d.lat],
                // pallino stimato: vuoto, col bordo del colore del valore
                getFillColor: d => (d.estimated ? WHITE : d.color),
                getLineColor: d => (d.estimated ? d.color : DOT_BORDER),
                // bordo in pixel: in metri (il default) zoomando molto copriva tutto il pallino di scuro
                lineWidthUnits: "pixels",
                getLineWidth: d => (d.estimated ? 3 : 1),
                stroked: true,
                radiusUnits: "pixels",
                getRadius: SAMPLE_RADIUS_PX,
                pickable: true,
            }),
        ];
        overlayRef.current.setProps({ layers });
    }, [segments, dots]);

    return null;
}
